import { z } from 'zod';

import type { graphql } from 'src/graphql';
import type { CombinedError } from 'urql';

export type IntegrationType = ReturnType<typeof graphql.scalar<'IntegrationType'>>;

export const INTEGRATION_CALLBACK_PATH = 'oauth/callback';

// Each URI must be on the API's redirect allowlist and registered with the provider.
const REDIRECT_ORIGINS = {
  PROD: 'https://app.askable.com',
  STAGE: 'https://staging.app.askable.com',
  DEV: 'https://app-dev.askable.com',
  PREVIEW: 'https://app-dev.askable.com',
  PR: 'https://app-dev.askable.com',
} as const;

const isRegisteredEnvironment = (environment: string | undefined): environment is keyof typeof REDIRECT_ORIGINS =>
  environment !== undefined && Object.hasOwn(REDIRECT_ORIGINS, environment);

/** Previews have their own origins, so they sign in through app-dev's callback. */
export const integrationRedirectUri = ({ environment, origin }: { environment?: string; origin: string }) => {
  const redirectOrigin = isRegisteredEnvironment(environment) ? REDIRECT_ORIGINS[environment] : origin;
  return `${redirectOrigin}/${INTEGRATION_CALLBACK_PATH}`;
};

/** Our own callback page, shown in the popup while the provider's sign-in URL is fetched. */
export const integrationSignInStartUrl = ({ integration, origin }: { integration: IntegrationType; origin: string }) =>
  `${origin}/${INTEGRATION_CALLBACK_PATH}?${new URLSearchParams({ integration }).toString()}`;

export const INTEGRATIONS_CHANNEL_NAME = 'askable-integrations';

const INTEGRATION_TYPES = ['google_calendar', 'microsoft_calendar'] as const satisfies readonly IntegrationType[];

const integrationTypeSchema = z.enum(INTEGRATION_TYPES);

const integrationChannelMessageSchema = z.object({
  type: z.enum(['connected', 'disconnected']),
  integration: integrationTypeSchema,
  signInId: z.string().optional(),
});

export type IntegrationChannelMessage = z.infer<typeof integrationChannelMessageSchema>;

export const parseIntegrationChannelMessage = (data: unknown): IntegrationChannelMessage | null => {
  const result = integrationChannelMessageSchema.safeParse(data);
  return result.success ? result.data : null;
};

export const parseIntegrationType = (value: unknown): IntegrationType | null => {
  const result = integrationTypeSchema.safeParse(value);
  return result.success ? result.data : null;
};

// The provider's redirect carries only code and state, so the popup keeps the integration for its copy,
// and the sign-in's id so only the tab that opened it says it worked.
const SIGN_IN_KEY = 'askable-integration-sign-in';

const signInSchema = z.object({ integration: integrationTypeSchema, signInId: z.string() });

type SignIn = z.infer<typeof signInSchema>;

export const rememberSignIn = (popup: Window, signIn: SignIn) => {
  try {
    popup.sessionStorage.setItem(SIGN_IN_KEY, JSON.stringify(signIn));
  } catch {
    // Storage is off in this popup, so the callback uses copy without the integration name.
  }
};

export const readSignIn = (): SignIn | null => {
  try {
    const result = signInSchema.safeParse(JSON.parse(window.sessionStorage.getItem(SIGN_IN_KEY) ?? 'null'));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
};

/** Calls `listener` for each valid message from any tab, this one included. Returns the unsubscribe. */
export const onIntegrationChange = (listener: (message: IntegrationChannelMessage) => void) => {
  const channel = new BroadcastChannel(INTEGRATIONS_CHANNEL_NAME);
  channel.addEventListener('message', event => {
    const message = parseIntegrationChannelMessage(event.data);
    if (message) {
      listener(message);
    }
  });
  return () => channel.close();
};

export const broadcastIntegrationChange = (message: IntegrationChannelMessage) => {
  const channel = new BroadcastChannel(INTEGRATIONS_CHANNEL_NAME);
  // oxlint-disable-next-line unicorn/require-post-message-target-origin -- BroadcastChannel.postMessage takes no target origin
  channel.postMessage(message);
  channel.close();
};

type IntegrationErrorCode =
  | 'INTEGRATION_PROVIDER_UNAVAILABLE'
  | 'INTEGRATION_SCOPES_MISSING'
  | 'INTEGRATION_STATE_INVALID';

export const hasIntegrationErrorCode = (error: CombinedError | undefined, code: IntegrationErrorCode) =>
  error?.graphQLErrors.some(graphQLError => graphQLError.extensions?.code === code) ?? false;
